/**
 * AuditService — Append-only audit log for Ley 29733 (Peru) compliance.
 *
 * Rules (ARCH-SEC-1):
 *  - NEVER throw — all errors go to console.warn only.
 *  - Fire-and-forget — callers do NOT await this.
 *  - firmId and userId are required; assertFirmId guards firmId.
 *  - changesJson: field names + new values only. Never passwords or tokens.
 *  - ip_hash: null in v1 — requires Edge Function for real IP.
 *  - Retention: 7 years (enforced by Supabase table policy, not this code).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { assertFirmId } from '../utils/assertFirmId';

export type AuditAction =
  | 'created'
  | 'updated'
  | 'posted'
  | 'exported'
  | 'viewed'
  | 'deleted_soft'
  | 'login'
  | 'logout'
  | 'password_recovery'
  | 'agent_run';

export interface AuditEventParams {
  action:       AuditAction;
  entityType:   string;
  entityId?:    string;
  changesJson?: Record<string, unknown>;
}

/**
 * Writes one row to audit_events.
 * Fire-and-forget: callers must NOT await this (use void).
 * Never throws — any failure is logged as console.warn.
 */
export async function logAuditEvent(
  supabase:  SupabaseClient,
  firmId:    string,
  userId:    string,
  params:    AuditEventParams,
): Promise<void> {
  try {
    // Guard: never write with an invalid firmId
    assertFirmId(firmId, 'AuditService.logAuditEvent');

    const { error } = await supabase.from('audit_events').insert({
      firm_id:      firmId,
      user_id:      userId,
      action:       params.action,
      entity_type:  params.entityType,
      entity_id:    params.entityId   ?? null,
      changes_json: params.changesJson ?? null,
      ip_hash:      null, // ip_hash: null in v1 — requires Edge Function for real IP
    });

    if (error) {
      console.warn('[AuditService] insert failed:', error.message);
    }
  } catch (err) {
    console.warn('[AuditService] unexpected error — primary operation unaffected:', err);
  }
}
